import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, MapPin, Banknote, Home } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { isRTL } from "@/i18n";
import { useCurrency } from "@/contexts/CurrencyContext";

const HERO_BG = "https://images.unsplash.com/photo-1641763077933-13a47029bf72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtZWRpdGVycmFuZWFuJTIwb2NlYW4lMjBjb2FzdHxlbnwwfHx8fDE3NzQ5MDA2MTl8MA&ixlib=rb-4.1.0&q=85";

export default function HeroSection() {
  const { t, i18n } = useTranslation();
  const { currency } = useCurrency();
  const [region, setRegion] = useState("");
  const [priceRange, setPriceRange] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const rtl = isRTL(i18n.language);

  return (
    <section data-testid="hero-section" className="relative min-h-[92vh] flex items-center overflow-hidden" dir={rtl ? "rtl" : "ltr"}>
      <div className="absolute inset-0">
        <img src={HERO_BG} alt="Mediterranean Coast" className="w-full h-full object-cover hero-bg" />
        <div className={`absolute inset-0 ${rtl ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-[#0A192F]/85 via-[#0A192F]/50 to-[#0A192F]/20`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/60 via-transparent to-transparent" />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-32">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-line" />
            <span className="text-[#D4AF37] text-sm font-medium tracking-[0.25em] uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {t("hero.pretitle")}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-[1.1] mb-6 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("hero.title_1")}{" "}
            <span className="text-[#D4AF37] italic">{t("hero.title_highlight")}</span>
            {t("hero.title_2") ? ` ${t("hero.title_2")}` : ""}
          </h1>

          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {t("hero.subtitle")}
          </p>
        </div>

        <div data-testid="hero-search-bar" className="bg-white/95 header-glass rounded-sm p-3 md:p-4 shadow-2xl max-w-4xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="flex items-center gap-3 px-3">
              <MapPin className="w-5 h-5 text-[#D4AF37] shrink-0" />
              <div className="w-full">
                <p className="text-[10px] uppercase tracking-widest text-[#0A192F]/50 font-medium mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t("hero.search.region")}
                </p>
                <Select value={region} onValueChange={setRegion}>
                  <SelectTrigger data-testid="search-region" className="border-0 shadow-none h-8 p-0 text-[#0A192F] font-medium text-sm focus:ring-0">
                    <SelectValue placeholder={t("hero.search.region_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(t("hero.search.regions", { returnObjects: true })).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className={`flex items-center gap-3 px-3 md:border-${rtl ? "r" : "l"} border-[#E2E8F0]`}>
              <Banknote className="w-5 h-5 text-[#D4AF37] shrink-0" />
              <div className="w-full">
                <p className="text-[10px] uppercase tracking-widest text-[#0A192F]/50 font-medium mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t("hero.search.price")} ({currency.symbol})
                </p>
                <Select value={priceRange} onValueChange={setPriceRange}>
                  <SelectTrigger data-testid="search-price" className="border-0 shadow-none h-8 p-0 text-[#0A192F] font-medium text-sm focus:ring-0">
                    <SelectValue placeholder={t("hero.search.price_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(t("hero.search.prices", { returnObjects: true })).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{currency.symbol} {val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className={`flex items-center gap-3 px-3 md:border-${rtl ? "r" : "l"} border-[#E2E8F0]`}>
              <Home className="w-5 h-5 text-[#D4AF37] shrink-0" />
              <div className="w-full">
                <p className="text-[10px] uppercase tracking-widest text-[#0A192F]/50 font-medium mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                  {t("hero.search.type")}
                </p>
                <Select value={propertyType} onValueChange={setPropertyType}>
                  <SelectTrigger data-testid="search-type" className="border-0 shadow-none h-8 p-0 text-[#0A192F] font-medium text-sm focus:ring-0">
                    <SelectValue placeholder={t("hero.search.type_placeholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(t("hero.search.types", { returnObjects: true })).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <button
              data-testid="hero-search-btn"
              className="flex items-center justify-center gap-2 bg-[#D4AF37] text-white font-medium px-6 py-3 rounded-sm hover:bg-[#B5952F] transition-colors duration-300 shadow-lg shadow-[#D4AF37]/20"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <Search className="w-4 h-4" />
              <span>{t("hero.search.button")}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
